import { Check, ChevronDown, Search } from "lucide-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

export type AutocompleteOption = {
  value: string;
  label: string;
  description?: string;
  keywords?: string[];
};

type AutocompleteSelectProps = {
  label: string;
  value: string;
  options: AutocompleteOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  allowCustom?: boolean;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  emptyMessage?: string;
  onQueryChange?: (query: string) => void;
};

function normalize(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr")
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "");
}

export function AutocompleteSelect({
  label,
  value,
  options,
  onChange,
  placeholder = "Ara veya seç",
  allowCustom = false,
  disabled = false,
  className = "",
  loading = false,
  emptyMessage = "Eşleşen seçenek bulunamadı.",
  onQueryChange,
}: AutocompleteSelectProps) {
  const inputId = useId();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((option) => option.value === value);
  const [query, setQuery] = useState(selectedOption?.label ?? value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setQuery(selectedOption?.label ?? value);
  }, [selectedOption?.label, value]);

  useEffect(() => {
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, []);

  const filteredOptions = useMemo(() => {
    const search = normalize(query);
    if (!search || selectedOption?.label === query) return options;
    return options.filter((option) =>
      normalize(
        [
          option.label,
          option.description,
          option.value,
          ...(option.keywords ?? []),
        ]
          .filter(Boolean)
          .join(" "),
      ).includes(search),
    );
  }, [options, query, selectedOption?.label]);

  const exactMatch = options.some(
    (option) => normalize(option.label) === normalize(query),
  );
  const customAvailable = allowCustom && query.trim().length > 0 && !exactMatch;
  const itemCount = filteredOptions.length + (customAvailable ? 1 : 0);

  const choose = (nextValue: string, nextLabel: string) => {
    onChange(nextValue);
    setQuery(nextLabel);
    setOpen(false);
    setActiveIndex(0);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) =>
        itemCount ? Math.min(current + 1, itemCount - 1) : 0,
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      setQuery(selectedOption?.label ?? value);
      return;
    }
    if (event.key !== "Enter" || !open) return;
    event.preventDefault();
    const option = filteredOptions[activeIndex];
    if (option) {
      choose(option.value, option.label);
    } else if (customAvailable) {
      choose(query.trim(), query.trim());
    }
  };

  return (
    <div ref={rootRef} className={`autocomplete-field ${className}`.trim()}>
      <label className="autocomplete-label" htmlFor={inputId}>
        {label}
      </label>
      <div className="autocomplete-control">
        <Search size={14} aria-hidden="true" />
        <input
          id={inputId}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={open}
          aria-activedescendant={
            open && itemCount ? `${listId}-option-${activeIndex}` : undefined
          }
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onFocus={() => {
            setOpen(true);
            setActiveIndex(0);
          }}
          onChange={(event) => {
            const nextQuery = event.target.value;
            setQuery(nextQuery);
            onQueryChange?.(nextQuery);
            setOpen(true);
            setActiveIndex(0);
            if (allowCustom) onChange(nextQuery);
            else if (!nextQuery) onChange("");
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            window.setTimeout(() => {
              if (!rootRef.current?.contains(document.activeElement)) {
                setOpen(false);
                if (!allowCustom) setQuery(selectedOption?.label ?? value);
              }
            }, 0);
          }}
        />
        <button
          type="button"
          className="autocomplete-toggle"
          aria-label={`${label} seçeneklerini ${open ? "kapat" : "aç"}`}
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
        >
          <ChevronDown size={14} />
        </button>
      </div>
      {open && !disabled ? (
        <div id={listId} className="autocomplete-menu" role="listbox">
          {filteredOptions.map((option, index) => (
            <button
              id={`${listId}-option-${index}`}
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`autocomplete-option ${
                index === activeIndex ? "is-active" : ""
              }`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(option.value, option.label)}
            >
              <span>
                <strong>{option.label}</strong>
                {option.description ? (
                  <small>{option.description}</small>
                ) : null}
              </span>
              {option.value === value ? <Check size={14} /> : null}
            </button>
          ))}
          {customAvailable ? (
            <button
              id={`${listId}-option-${filteredOptions.length}`}
              type="button"
              role="option"
              aria-selected="false"
              className={`autocomplete-option autocomplete-custom ${
                activeIndex === filteredOptions.length ? "is-active" : ""
              }`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(filteredOptions.length)}
              onClick={() => choose(query.trim(), query.trim())}
            >
              <span>
                <strong>“{query.trim()}” değerini kullan</strong>
                <small>Yeni bir seçenek olarak kaydedilir</small>
              </span>
            </button>
          ) : null}
          {!filteredOptions.length && !customAvailable ? (
            <div className="autocomplete-empty">
              {loading ? "Aranıyor…" : emptyMessage}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
